import {
  ArticleMixTagsFormikValues,
  ArticleStrapiFilters,
  ArticleStrapiFiltersTag,
  ArticleTagsFormikValues,
  ArticlesSort,
} from '@tmh-website/services/article.service.types';
import {
  isChargepilotProductUpdateArticlePage,
  isChargepilotProductUpdateArticlePath,
  isElectricityTariffBlogArticlePage,
  isEnergyKnowledgeCenterArticlePage,
  isEnergyKnowledgeCenterArticlePath,
  isEnergyNewsArticlePage,
  isEnergyNewsArticlePath,
  isEnergyReferenceArticlePage,
  isKnowledgeCenterArticlePage,
  isKnowledgeCenterArticlePath,
  isKnowledgeCenterWebinarPage,
  isKnowledgeCenterWebinarPath,
  isKnowledgeCenterWhitepaperPage,
  isKnowledgeCenterWhitepaperPath,
  isNewsArticlePage,
  isNewsArticlePath,
  isReferenceArticlePage,
} from '@tmh-website/services/page.service';
import Yup from '@tmh-website/services/validation.service';
import {
  AnyPage,
  ArticleGroup,
  ArticlePageAsEntry,
  ArticleType,
  ArticleTypeToPublishDateFieldMap,
} from '@tmh-website/shared/cms';
import { Audience, LocalePrefix, Project, hasAudiences } from '@tmh-website/shared/const';
import format from 'date-fns/format';

export const ArticlesFiltersValidation = Yup.object({
  sort: Yup.string().oneOf(Object.values(ArticlesSort)),
  tags: Yup.object().shape({}),
});

/**
 * Get the relevant publish date from the page e.g. for sorting by date
 */
export function getDateFromPage(page: ArticlePageAsEntry): string {
  if (isEnergyKnowledgeCenterArticlePage(page as AnyPage)) {
    return page[ArticleTypeToPublishDateFieldMap[ArticleType.EnergyKnowledgeCenterArticle]];
  }
  if (isEnergyNewsArticlePage(page as AnyPage)) {
    return page[ArticleTypeToPublishDateFieldMap[ArticleType.EnergyNewsArticle]];
  }
  if (isEnergyReferenceArticlePage(page as AnyPage)) {
    const dateString = page[ArticleTypeToPublishDateFieldMap[ArticleType.EnergyReferenceArticle]];
    return dateString ? format(new Date(dateString), 'yyyy-MM-dd') : '';
  }
  if (isKnowledgeCenterArticlePage(page as AnyPage)) {
    return page[ArticleTypeToPublishDateFieldMap[ArticleType.KnowledgeCenterArticle]];
  }
  if (isKnowledgeCenterWebinarPage(page as AnyPage)) {
    return page[ArticleTypeToPublishDateFieldMap[ArticleType.KnowledgeCenterWebinar]];
  }
  if (isKnowledgeCenterWhitepaperPage(page as AnyPage)) {
    const dateString = page[ArticleTypeToPublishDateFieldMap[ArticleType.KnowledgeCenterWhitepaper]];
    return dateString ? format(new Date(dateString), 'yyyy-MM-dd') : '';
  }
  if (isNewsArticlePage(page as AnyPage)) {
    return page[ArticleTypeToPublishDateFieldMap[ArticleType.NewsArticle]];
  }
  if (isReferenceArticlePage(page as AnyPage)) {
    const dateString = page[ArticleTypeToPublishDateFieldMap[ArticleType.ReferenceArticle]];
    return dateString ? format(new Date(dateString), 'yyyy-MM-dd') : '';
  }
  if (isElectricityTariffBlogArticlePage(page as AnyPage)) {
    return page[ArticleTypeToPublishDateFieldMap[ArticleType.ElectricityTariffBlogArticle]];
  }
  if (isChargepilotProductUpdateArticlePage(page as AnyPage)) {
    return page[ArticleTypeToPublishDateFieldMap[ArticleType.ChargepilotProductUpdateArticle]];
  }
  return '';
}

export const getArticleTypeFromPath = (path: string, locale: LocalePrefix): ArticleType | undefined => {
  if (isEnergyKnowledgeCenterArticlePath(path, locale)) {
    return ArticleType.EnergyKnowledgeCenterArticle;
  }

  if (isEnergyNewsArticlePath(path, locale)) {
    return ArticleType.EnergyNewsArticle;
  }

  if (isKnowledgeCenterArticlePath(path, locale)) {
    return ArticleType.KnowledgeCenterArticle;
  }

  if (isKnowledgeCenterWebinarPath(path, locale)) {
    return ArticleType.KnowledgeCenterWebinar;
  }

  if (isKnowledgeCenterWhitepaperPath(path, locale)) {
    return ArticleType.KnowledgeCenterWhitepaper;
  }

  if (isNewsArticlePath(path, locale)) {
    return ArticleType.NewsArticle;
  }

  if (isChargepilotProductUpdateArticlePath(path, locale)) {
    return ArticleType.ChargepilotProductUpdateArticle;
  }

  return;
};

/**
 * The strapi filter object for articles
 */
export function getArticleStrapiFilters(
  audience: Audience,
  locale: LocalePrefix,
  project: Project,
  tags?: ArticleTagsFormikValues
): ArticleStrapiFilters | undefined {
  const filters: ArticleStrapiFilters['$and'][number][] = [];
  const andFilter: ArticleStrapiFiltersTag[] = [];
  if (tags) {
    Object.keys(tags).forEach(subject => {
      if (tags[subject].length > 0) {
        andFilter.push({
          tags: {
            group: { $eq: subject },
            value: { $in: tags[subject] },
          },
        });
      }
    });
  }
  if (andFilter.length > 0) {
    filters.push({ $and: andFilter });
  }

  if (hasAudiences(locale, project)) {
    filters.push({ audiences: { key: { $eq: audience } } });
  }

  if (filters.length === 0) {
    return;
  } else {
    return { $and: filters };
  }
}

export function getArticleMixStrapiFilters(
  audience: Audience,
  locale: LocalePrefix,
  project: Project,
  tags?: ArticleMixTagsFormikValues
): Record<ArticleGroup, ArticleStrapiFilters> | undefined {
  const result: Record<ArticleGroup, ArticleStrapiFilters> = {
    [ArticleGroup.Chargepilot]: { $and: [] },
    [ArticleGroup.ElectricityTariffBlogArticle]: { $and: [] },
    [ArticleGroup.EnergyKnowledgeCenterArticle]: { $and: [] },
    [ArticleGroup.EnergyKnowledgeCenterWebinar]: { $and: [] },
    [ArticleGroup.EnergyKnowledgeCenterWhitepaper]: { $and: [] },
    [ArticleGroup.EnergyNewsArticle]: { $and: [] },
    [ArticleGroup.EnergyReferenceArticle]: { $and: [] },
    [ArticleGroup.KnowledgeCenter]: { $and: [] },
    [ArticleGroup.News]: { $and: [] },
    [ArticleGroup.Reference]: { $and: [] },
    [ArticleGroup.Styleguide]: { $and: [] },
  };

  const addAudiencesFilter = (group: ArticleGroup): void => {
    result[group].$and.push({ audiences: { key: { $eq: audience } } });
  };

  const addTagFilters = (subject: string, tagValues: Record<string, string[]>): void => {
    const tagFilters: ArticleStrapiFiltersTag[] = [];

    Object.entries(tagValues).forEach(([group, values]) => {
      values.forEach(tagValue => {
        tagFilters.push({
          tags: {
            group: { $eq: group },
            value: { $in: [tagValue] },
          },
        });
      });
    });

    if (tagFilters.length > 0) {
      result[subject].$and.push({ $and: tagFilters });
    }
  };

  if (tags) {
    Object.entries(tags).forEach(([subject, tagValues]) => {
      if (tagValues) {
        addTagFilters(subject, tagValues);
      }
    });
  }

  if (hasAudiences(locale, project)) {
    Object.keys(result).forEach(group => addAudiencesFilter(group as ArticleGroup));
  }

  const hasFilters = Object.values(result).some(groupFilters => groupFilters.$and.length > 0);

  return hasFilters ? result : undefined;
}
